import fetch from 'isomorphic-unfetch'
import { v4 as uuidv4 } from 'uuid'

import { TENANTS } from '@/lib/tenants/constants'
import { Listing } from '@/types/graphql-generated-types'

const API_BASE_URL: string = process.env.NEXT_PUBLIC_GLUGLU_API_ENDPOINT!

const httpRequest = async <Payload extends unknown>(
  url: string,
  payload: Payload,
  method: RequestInit['method'] = 'POST'
) => {
  const getHeaders = async (): Promise<Headers> => ({
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-Request-ID': uuidv4(),
  })

  return fetch(url, {
    body: JSON.stringify(payload),
    headers: await getHeaders(),
    method,
  })
}

type Headers = Record<string, string>
export type FavouriteSearchOrigin = 'SEARCH_RESULTS' | 'LISTING_CONTACT'

export type ContactAgencyPayload = {
  currentLanguage: Locale
  lead: {
    consentForCasavoMortgageService: boolean
    email: string
    hasAcceptedMarketing: boolean
    message: string
    phoneNumber: string
  }
  listingId: Listing['id']
  trackingClientId: string | null
}

export type ContactCasavoPayload = {
  consentForCasavoMortgageService: boolean
  currentLanguage: Locale
  email: string
  keepMeUpdated: boolean
  name: string | null
  phoneNumber: string
  reason: string | null
  trackingClientId: string | null
}

type PotentialPropertyVisitPayload =
  | {
      crm_contact_id?: string
      email: string
      name: string
      phone: string
      score?: string
      tenant_id: typeof TENANTS.casavo
    }
  | {
      contact_id: string
      tenant_id: typeof TENANTS.ucsc
    }

export type UnsubscribeSource = 'similar_listing' | 'potential_property' | 'inbox_property_recommendation'

export type GlugluApiClient = {
  contactAgency: (payload: ContactAgencyPayload) => Promise<void>
  listingInfo: (listingId: Listing['id'], payload: ContactCasavoPayload) => Promise<void>
  potentialPropertyVisitInterest: (transactionId: string, payload: PotentialPropertyVisitPayload) => Promise<void>
  unsubscribe: (source: UnsubscribeSource) => (email: string) => Promise<null>
  unsubscribeFavoriteSearch: (listingId: Listing['id']) => Promise<null>
}

export const initApi = (baseURL: string = API_BASE_URL): GlugluApiClient => ({
  async contactAgency(payload) {
    try {
      const response = await httpRequest(`${baseURL}/contact-agency`, payload, 'POST')
      if (!response.ok) {
        throw Error('Error while sending email. Service returned 500.')
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e.message)
      throw e.message
    }
  },
  async listingInfo(listingId, payload) {
    try {
      const response = await httpRequest(`${baseURL}/listing/${listingId}/request-info`, payload, 'POST')
      if (!response.ok) {
        throw Error('Error while sending email. Service returned 500.')
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e.message)
      throw e.message
    }
  },

  potentialPropertyVisitInterest: async (transactionId: string, payload: PotentialPropertyVisitPayload) => {
    try {
      const response = await httpRequest<PotentialPropertyVisitPayload>(
        `${baseURL}/potential-properties/${transactionId}/express-interest-in-visit`,
        payload,
        'POST'
      )
      if (!response.ok) {
        throw Error(`Error during sending visit interest ${response.status} ${response.statusText}`)
      }
    } catch (e) {
      console.error(e.message)
      throw e.message
    }
  },

  unsubscribe(source) {
    return async function (email) {
      const response = await httpRequest(`${baseURL}/users/${email}/${source}`, {}, 'DELETE')
      if (!response.ok) {
        throw new Error(response.status + ' response on favorite search submit')
      } else {
        return null
      }
    }
  },
  async unsubscribeFavoriteSearch(id: string) {
    const response = await httpRequest(`${baseURL}/favorite-search/${id}`, {}, 'DELETE')
    if (!response.ok) {
      throw new Error(response.status + ' response on favorite search unsubscribe')
    } else {
      return null
    }
  },
})

interface BuyerInterestFlowPayload {
  contact_info: {
    current_language: string
    email: string
    full_name: string
    phone: string
  }
  interests: {
    geo_slugs: string[]
    max_budget: number
    number_of_rooms: string[]
    search_status: string
  }
}

export interface BuyerInterestFlowClient {
  submit(payload: BuyerInterestFlowPayload): Promise<void>
}

export const buyerInterestFlowClient = (baseURL: string = API_BASE_URL): BuyerInterestFlowClient => ({
  async submit(payload: BuyerInterestFlowPayload) {
    const response = await fetch(`${baseURL}/buyer-interests`, {
      body: JSON.stringify(payload),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Request-ID': uuidv4(),
      },
      method: 'POST',
    })

    if (!response.ok) {
      throw new Error(`${response.status} error while sending request. Try again.`)
    }
  },
})

export default initApi()
